"use client"

import { useState, useEffect, useCallback } from "react"
import { createClient } from "@/lib/supabase/client"
import { useAuth } from "@/lib/hooks/use-auth"

export interface Goal {
  id: string
  title: string
  description: string | null
  status: string
  target_date: string | null
  created_at: string
  progress: number
}

export interface GoalSignal {
  id: string
  label: string
  value: string
}

export interface GoalConstraint {
  id: string
  text: string
}

export interface GoalMemory {
  id: string
  title: string
  domain?: string
}

// MVP storage keys for signals/constraints (stored in localStorage until DB schema ready)
const SIGNALS_STORAGE_KEY = "logosiq.goalSignals"
const CONSTRAINTS_STORAGE_KEY = "logosiq.goalConstraints"

export interface UseGoalsReturn {
  // Active goal
  activeGoal: Goal | null
  isLoading: boolean
  error: string | null
  
  // Signals (lightweight metrics)
  signals: GoalSignal[]
  addSignal: (label: string, value: string) => void
  updateSignal: (id: string, label: string, value: string) => void
  removeSignal: (id: string) => void
  
  // Constraints
  constraints: GoalConstraint[]
  addConstraint: (text: string) => void
  updateConstraint: (id: string, text: string) => void
  removeConstraint: (id: string) => void
  
  // Related memories
  relatedMemories: GoalMemory[]
  
  // Goal management
  updateGoal: (title: string, description: string | null) => Promise<void>
  
  // Progress status
  progressStatus: "drafted" | "in_motion" | "review"
  
  // Refetch
  refetch: () => Promise<void>
}

function getStoredSignals(): GoalSignal[] {
  if (typeof window === "undefined") return []
  try {
    const stored = localStorage.getItem(SIGNALS_STORAGE_KEY)
    return stored ? JSON.parse(stored) : []
  } catch {
    return []
  }
}

function setStoredSignals(signals: GoalSignal[]): void {
  if (typeof window === "undefined") return
  try {
    localStorage.setItem(SIGNALS_STORAGE_KEY, JSON.stringify(signals))
  } catch {
    // Silently fail
  }
}

function getStoredConstraints(): GoalConstraint[] {
  if (typeof window === "undefined") return []
  try {
    const stored = localStorage.getItem(CONSTRAINTS_STORAGE_KEY)
    return stored ? JSON.parse(stored) : []
  } catch {
    return []
  }
}

function setStoredConstraints(constraints: GoalConstraint[]): void {
  if (typeof window === "undefined") return
  try {
    localStorage.setItem(CONSTRAINTS_STORAGE_KEY, JSON.stringify(constraints))
  } catch {
    // Silently fail
  }
}

export function useGoals(): UseGoalsReturn {
  const supabase = createClient()
  const { user } = useAuth()
  
  const [activeGoal, setActiveGoal] = useState<Goal | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  
  const [signals, setSignals] = useState<GoalSignal[]>([])
  const [constraints, setConstraints] = useState<GoalConstraint[]>([])
  const [relatedMemories, setRelatedMemories] = useState<GoalMemory[]>([])

  const fetchGoals = useCallback(async () => {
    if (!user) {
      setIsLoading(false)
      return
    }

    try {
      setIsLoading(true)
      setError(null)

      // Fetch active goal
      const { data: goals, error: goalError } = await supabase
        .from("goals")
        .select("*")
        .eq("user_id", user.id)
        .in("status", ["active", "in_progress"])
        .order("created_at", { ascending: false })
        .limit(1)

      if (goalError) {
        console.error("[useGoals] Failed to fetch goals:", goalError)
        setError("Failed to load goals")
      } else if (goals && goals.length > 0) {
        setActiveGoal(goals[0])
        
        // Fetch related memories (semantic memories linked to goal domain)
        const { data: memories } = await supabase
          .from("semantic_memories")
          .select("id, title, domains")
          .eq("user_id", user.id)
          .eq("is_archived", false)
          .limit(5)
        
        if (memories) {
          setRelatedMemories(
            memories.map((m) => ({
              id: m.id,
              title: m.title,
              domain: m.domains?.[0],
            }))
          )
        }
      } else {
        setActiveGoal(null)
      }

      // Load MVP-safe local storage for signals/constraints
      setSignals(getStoredSignals())
      setConstraints(getStoredConstraints())

    } catch (err) {
      console.error("[useGoals] Exception:", err)
      setError("An error occurred while loading goals")
    } finally {
      setIsLoading(false)
    }
  }, [user, supabase])

  useEffect(() => {
    fetchGoals()
  }, [fetchGoals])

  // Signals management
  const addSignal = useCallback((label: string, value: string) => {
    const newSignal: GoalSignal = {
      id: crypto.randomUUID(),
      label,
      value,
    }
    setSignals((prev) => {
      const updated = [...prev, newSignal].slice(0, 3) // Max 3 signals
      setStoredSignals(updated)
      return updated
    })
  }, [])

  const updateSignal = useCallback((id: string, label: string, value: string) => {
    setSignals((prev) => {
      const updated = prev.map((s) => (s.id === id ? { ...s, label, value } : s))
      setStoredSignals(updated)
      return updated
    })
  }, [])

  const removeSignal = useCallback((id: string) => {
    setSignals((prev) => {
      const updated = prev.filter((s) => s.id !== id)
      setStoredSignals(updated)
      return updated
    })
  }, [])

  // Constraints management
  const addConstraint = useCallback((text: string) => {
    const newConstraint: GoalConstraint = {
      id: crypto.randomUUID(),
      text,
    }
    setConstraints((prev) => {
      const updated = [...prev, newConstraint].slice(0, 6) // Max 6 constraints
      setStoredConstraints(updated)
      return updated
    })
  }, [])

  const updateConstraint = useCallback((id: string, text: string) => {
    setConstraints((prev) => {
      const updated = prev.map((c) => (c.id === id ? { ...c, text } : c))
      setStoredConstraints(updated)
      return updated
    })
  }, [])

  const removeConstraint = useCallback((id: string) => {
    setConstraints((prev) => {
      const updated = prev.filter((c) => c.id !== id)
      setStoredConstraints(updated)
      return updated
    })
  }, [])

  // Update goal
  const updateGoal = useCallback(async (title: string, description: string | null) => {
    if (!activeGoal || !user) return

    try {
      const { error: updateError } = await supabase
        .from("goals")
        .update({
          title,
          description,
          updated_at: new Date().toISOString(),
        })
        .eq("id", activeGoal.id)

      if (updateError) {
        console.error("[useGoals] Failed to update goal:", updateError)
        throw updateError
      }

      setActiveGoal((prev) => (prev ? { ...prev, title, description } : null))
    } catch (err) {
      console.error("[useGoals] Exception updating goal:", err)
      throw err
    }
  }, [activeGoal, user, supabase])

  // Compute progress status heuristically
  const progressStatus: "drafted" | "in_motion" | "review" = (() => {
    if (!activeGoal) return "drafted"
    if (constraints.length > 0 && relatedMemories.length > 0) return "review"
    if (constraints.length > 0 || signals.length > 0) return "in_motion"
    return "drafted"
  })()

  return {
    activeGoal,
    isLoading,
    error,
    signals,
    addSignal,
    updateSignal,
    removeSignal,
    constraints,
    addConstraint,
    updateConstraint,
    removeConstraint,
    relatedMemories,
    updateGoal,
    progressStatus,
    refetch: fetchGoals,
  }
}
